import React, { useMemo, useState, useEffect } from 'react';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Anchor,
  Center,
  Group,
  Stack,
  Switch,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../SQLEditorPopup';

const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder>
    <Text size="sm" fw={500} mb={4}>
      Legend
    </Text>
    <Stack gap={4}>
      {[
        { color: 'var(--mantine-color-green-6)', label: 'Margin ≥ 20%' },
        { color: 'var(--mantine-color-yellow-6)', label: 'Margin between 15% and 20%' },
        { color: 'var(--mantine-color-red-6)', label: 'Margin < 15%' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

function PurchasingIndividualRevenueDetails() {
  const { ccode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });

  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  // Build URL with ccode, period, and filter parameters
  const url = `${config.apiBaseUrl}/supply-chain/purchasing/individual/scorecard/inventory-revenue-details?ccode=${ccode}&period=${period}&filter=${warehouseFilter}`;
  const { data, isLoading, error } = useAuthenticatedData(url);
  const { data: purchaserName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  const handleTabChange = (newPeriod) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('period', newPeriod);
    newSearchParams.set('filter', warehouseFilter);
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index,
      ...item.values,
    }));
  }, [data]);

  const generateExportFileName = () => `Purchasing-${purchaserName}-${period}-revenue-details`;

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Buyer': row.fullname || '',
      'Pentagon Doc#': row.docno || '',
      'Line': row.userline || '',
      'Invoice Date': row.invoicedate ? new Date(row.invoicedate).toLocaleDateString() : '',
      'Part Number': row.partnumber || '',
      'Condition': row.condition || '',
      'Qty Shipped': row.qtyshipped || '',
      'Revenue': row.revenue || '',
      'Cost': row.cost || '',
      'Margin ($)': row.margindollars || '',
      'Margin %': row.marginpercent ? `${Number(row.marginpercent).toFixed(2)}%` : '',
      'Projected Revenue': row.projectedrevenue || '',
      'Projected Margin ($)': row.projectedmargindollars || '',
      'Projected Margin %': row.projectedmarginpercent ? `${Number(row.projectedmarginpercent).toFixed(2)}%` : '',
      'DOS': row.dos || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  const columns = useMemo(
    () => [
      { accessorKey: 'fullname', header: 'Buyer', size: 100 },
      { accessorKey: 'docno', header: 'Pentagon Doc#', size: 120 },
      { accessorKey: 'userline', header: 'Line', size: 80 },
      {
        accessorKey: 'invoicedate',
        header: 'Invoice Date',
        size: 180,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      { accessorKey: 'condition', header: 'Condition', size: 100 },
      {
        accessorKey: 'qtyshipped',
        header: 'Qty Shipped',
        size: 120,
      },
      {
        accessorKey: 'revenue',
        header: 'Revenue',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'cost',
        header: 'Cost',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : 'N/A',
      },
      {
        accessorKey: 'margindollars',
        header: 'Margin ($)',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'marginpercent',
        header: 'Margin %',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
      {
        accessorKey: 'projectedrevenue',
        header: 'Projected Revenue',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedmargindollars',
        header: 'Projected Margin ($)',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedmarginpercent',
        header: 'Projected Margin %',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
      {
        accessorKey: 'dos',
        header: 'DOS',
        size: 120,
      },
    ],
    []
  );

  const getRowStyle = (row) => {
    const margin = Number(row.original.marginpercent);
    if (isNaN(margin)) return {};
    
    if (margin >= 20) {
      return {
        backgroundColor: 'var(--mantine-color-green-1)',
        color: 'var(--mantine-color-green-9)',
      };
    }
    if (margin >= 15 && margin < 20) {
      return {
        backgroundColor: 'var(--mantine-color-yellow-1)',
        color: 'var(--mantine-color-yellow-9)',
      };
    }
    if (margin < 15) {
      return {
        backgroundColor: 'var(--mantine-color-red-1)',
        color: 'var(--mantine-color-red-9)',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header with title and checkbox */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Individual Scorecard - Revenue Details - {isLoadingName ? 'Loading...' : purchaserName}
        </Text>
        
        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_individual_revenue_margin_summary',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_individual_revenue_margin_details',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="stretch" grow>
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Text size="sm" fw={500} mb={4}>
            About the Data
          </Text>
          <Text size="xs" c="dimmed">
            These are invoices for parts that {isLoadingName ? 'this purchaser' : purchaserName} purchased on a PO for stock. For each sale we show our revenue, the original expense of the part, and the margin between the two. The margin is shown as both a percentage and a dollar amount.
          </Text>
        </Card>
        <ColorLegend />
      </Group>

      <Box mt="sm">
        <Tabs value={period} onChange={handleTabChange} mb="sm">
          <Tabs.List>
            <Tabs.Tab value="MTD">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="QTD">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="YTD">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      <Paper shadow="sm" p="sm" style={{ height: 600 }}>
        <Box style={{ height: '100%' }}>
          {isLoading || isLoadingName ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : errorName ? (
            <Alert color="red" title="Error">
              {errorName.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>
    </Paper>
  );
}

export default PurchasingIndividualRevenueDetails;